import * as React from 'react';
import classNames from 'classnames';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import Text from '../../atoms/Text';
import Icon from '../../atoms/Icon';

import styles from './ProgressTracker.module.scss';

// Interfaces
// //////////////////////////

export interface TrackerStep {
  className?: string;
  id: string;
  title: string;
  status: 'complete' | 'active' | 'ongoing' | 'pending' | 'current';
  content?: Record<string, string | undefined>;
  icon?: IconProp;
  children?: React.ReactNode;
}

export interface Props extends React.HTMLAttributes<HTMLUListElement> {
  steps: Array<TrackerStep>;
  theme?: 'default';
  animation?: 'ripple' | 'none';
  vertical?: boolean;
}

/**
 * <ProgressTracker> component.
 */
const ProgressTracker: React.FunctionComponent<Props> = ({
  className = '',
  steps,
  theme = 'default',
  animation = 'ripple',
  vertical = false,
  ...htmlElementProps
}: Props) => {
  // Get the tracker class name
  const trackerCls = classNames(styles['progress-tracker'], className, {
    'is-vertical': vertical,
    'is-center': !vertical,
    [`theme-${theme}`]: theme,
  });

  return (
    <ul className={trackerCls} {...htmlElementProps}>
      {steps &&
        steps.map((step) => {
          const stepCls = classNames(styles['progress-step'], step.className, {
            [`anim-${animation}`]: true,
            'is-complete': step.status === 'complete',
            'is-active': step.status === 'active',
            'is-pending': step.status === 'pending',
            'is-current': step.status === 'current',
          });

          return (
            <li className={stepCls} key={step.id}>
              <span className={styles['progress-marker']}>
                {step.icon && (
                  <div className={styles['progress-icon-container']}>
                    <Icon
                      className={styles['progress-marker-icon']}
                      icon={step.icon}
                      color="white"
                    />
                  </div>
                )}
              </span>
              <span
                className={classNames(styles['progress-text'], {
                  [styles['progress-text-icon-adjust']]: step.icon,
                })}
              >
                <Text
                  className={classNames(styles['progress-text-title'], {
                    'is-disabled': !vertical && step.status === 'pending',
                  })}
                  size="sm2"
                  weight="bold"
                  color="primary"
                >
                  {step.title}
                </Text>

                {step.content &&
                  Object.entries(step.content)
                    .filter(([, value]) => !!value)
                    .map(([key, value]) =>
                      key ? (
                        <div key={key}>
                          <b className={styles['step-content-title']}>
                            <Text as="span" size="sm1">
                              {value ? `${key}:` : key}
                            </Text>
                          </b>

                          <Text as="span" size="sm1" weight="bold">
                            {value}
                          </Text>
                        </div>
                      ) : (
                        <div key={value}>
                          <span className="mt-1">
                            <Text as="span" size="sm1">
                              {value}
                            </Text>
                          </span>
                        </div>
                      ),
                    )}
              </span>

              {step.children && (
                <span className={styles['progress-child']}>
                  {step.children}
                </span>
              )}
            </li>
          );
        })}
    </ul>
  );
};

export default ProgressTracker;
